import React, { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Mic, Bell, CheckCircle2, ChevronRight } from 'lucide-react';

import { API } from '@/App';

const OnboardingFlow = ({ onComplete, token }) => {
  const [step, setStep] = useState(1);
  const [micStatus, setMicStatus] = useState('prompt'); // prompt, granted, denied

  
  // Verificar estados actuales al cargar
  useEffect(() => {
    // Revisar notificaciones
    if ('Notification' in window) {
      // Notification.permission; // No necesitamos guardarlo si no se usa para lógica visual específica
    }
    
    // Revisar micrófono (solo navegadores modernos soportan query)
    if (navigator.permissions && navigator.permissions.query) {
      navigator.permissions.query({ name: 'microphone' })
        .then(res => setMicStatus(res.state))
        .catch(() => {});
    }
  }, []);

  const requestMic = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      // Detener las pistas inmediatamente, solo queríamos el permiso
      stream.getTracks().forEach(track => track.stop());
      setMicStatus('granted');
      setTimeout(() => setStep(2), 600);
    } catch (err) {
      console.warn("Permiso de micrófono denegado:", err);
      setMicStatus('denied');
      // Aún así avanzamos, el usuario decidió no darlo
      setTimeout(() => setStep(2), 1500);
    }
  };

  const requestNotif = async () => {
    try {
      const permission = await Notification.requestPermission();
      
      if (permission === 'granted') {
        // Aprovechar y suscribir la PWA o FCM
        await subscribeUserToPush();
      }
      
      setTimeout(() => setStep(3), 600);
    } catch (err) {
      console.error(err);
      setTimeout(() => setStep(3), 1000);
    }
  };

  const subscribeUserToPush = async () => {
    try {
      if (!('serviceWorker' in navigator) || !('PushManager' in window)) return;
      const vapidResponse = await fetch(`${API}/notifications/vapid-public-key`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!vapidResponse.ok) return;
      const { publicKey } = await vapidResponse.json();

      const padding = '='.repeat((4 - publicKey.length % 4) % 4);
      const base64 = (publicKey + padding).replace(/-/g, '+').replace(/_/g, '/');
      const rawData = window.atob(base64);
      const outputArray = new Uint8Array(rawData.length);
      for (let i = 0; i < rawData.length; ++i) {
        outputArray[i] = rawData.charCodeAt(i);
      }

      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: outputArray
      });

      await fetch(`${API}/notifications/subscribe`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ subscription: subscription.toJSON() })
      });
    } catch (e) {
      console.warn('Error silencioso al suscribir notificaciones en onboarding:', e);
    }
  };

  const handleFinish = () => {
    localStorage.setItem('pgastos_onboarding_done', 'true');
    onComplete();
  };

  return (
    <div className="fixed inset-0 z-[100] bg-slate-900/80 backdrop-blur-md flex items-center justify-center p-4">
      <Card className="w-full max-w-md bg-white border-0 shadow-2xl overflow-hidden animate-in zoom-in-95 duration-300">
        
        {/* Progreso */}
        <div className="h-1.5 w-full bg-slate-100">
          <div 
            className="h-full bg-blue-600 transition-all duration-500 ease-out"
            style={{ width: `${(step / 3) * 100}%` }}
          />
        </div>

        <div className="p-8">
          
          {step === 1 && (
            <div className="space-y-6 text-center animate-in slide-in-from-right-8 duration-500">
              <div className="w-20 h-20 mx-auto bg-blue-100 rounded-full flex items-center justify-center">
                <Mic className="w-10 h-10 text-blue-600" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-slate-900 mb-2">Permiso de Micrófono</h2>
                <p className="text-slate-600 text-sm leading-relaxed">
                  Para registrar tus gastos con la voz usando nuestra IA, necesitamos acceso a tu micrófono. Solo lo usaremos cuando tú aprietes el botón de grabar.
                </p>
              </div>

              <div className="pt-4 space-y-3">
                <Button 
                  onClick={requestMic} 
                  className="w-full h-12 bg-blue-600 hover:bg-blue-700 text-white shadow-lg shadow-blue-200"
                >
                  Permitir Micrófono
                </Button>
                <button 
                  onClick={() => setStep(2)}
                  className="w-full h-10 text-sm font-medium text-slate-500 hover:text-slate-700 transition-colors"
                >
                  Omitir por ahora
                </button>
              </div>
              
              {micStatus === 'denied' && (
                <p className="text-xs text-red-500 font-medium">Permiso denegado. Podrás activarlo después en los ajustes de tu navegador.</p>
              )}
            </div>
          )}

          {step === 2 && (
            <div className="space-y-6 text-center animate-in slide-in-from-right-8 duration-500">
              <div className="w-20 h-20 mx-auto bg-purple-100 rounded-full flex items-center justify-center">
                <Bell className="w-10 h-10 text-purple-600" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-slate-900 mb-2">Activar Notificaciones</h2>
                <p className="text-slate-600 text-sm leading-relaxed">
                  Mantente al día con recordatorios de pago y alertas de presupuesto. No te enviaremos spam, lo prometemos.
                </p>
              </div>

              <div className="pt-4 space-y-3">
                <Button 
                  onClick={requestNotif} 
                  className="w-full h-12 bg-purple-600 hover:bg-purple-700 text-white shadow-lg shadow-purple-200"
                >
                  Habilitar Notificaciones
                </Button>
                <button 
                  onClick={() => setStep(3)}
                  className="w-full h-10 text-sm font-medium text-slate-500 hover:text-slate-700 transition-colors"
                >
                  Quizás más tarde
                </button>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-6 text-center animate-in zoom-in duration-500">
              <div className="w-20 h-20 mx-auto bg-green-100 rounded-full flex items-center justify-center">
                <CheckCircle2 className="w-10 h-10 text-green-600" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-slate-900 mb-2">¡Todo listo!</h2>
                <p className="text-slate-600 text-sm leading-relaxed">
                  Ya configuraste los permisos principales. Ahora disfruta de la experiencia completa de Portal de Gastos.
                </p>
              </div>

              <div className="pt-4">
                <Button 
                  onClick={handleFinish} 
                  className="w-full h-12 bg-slate-900 hover:bg-slate-800 text-white"
                >
                  Ir al Dashboard <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          )}

        </div>
      </Card>
    </div>
  );
};

export default OnboardingFlow;
